import { Heart, Shield, Eye, Users, Target, HandHeart, Sparkles, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import './values-premium.css';

const values = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We serve with love and empathy, seeing the humanity in every person we encounter.',
    color: 'bg-red-50 text-red-600',
  },
  {
    icon: Shield,
    title: 'Dignity',
    description: 'Every person deserves respect and humane treatment, regardless of their circumstances.',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: Eye,
    title: 'Integrity',
    description: 'We are accountable and transparent in our work, honest in all our dealings.',
    color: 'bg-purple-50 text-purple-600',
  },
  {
    icon: Users,
    title: 'Inclusion',
    description: 'Everyone matters; no one is excluded from the reach of our compassion and service.',
    color: 'bg-green-50 text-green-600',
  },
  {
    icon: Target,
    title: 'Impact',
    description: 'We pursue measurable, lasting community transformation that creates real change.',
    color: 'bg-amber-50 text-amber-600',
  },
  {
    icon: HandHeart,
    title: 'Service',
    description: 'We see service as a responsibility, not convenience—a calling, not an option.',
    color: 'bg-teal-50 text-teal-600',
  },
];

export const Values = () => {
  return (
    <div data-testid="values-page" className="bg-white text-stone-900">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-36 text-white">
        {/* hero image from public/assets */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/values-hero.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/90 via-blue-950/70 to-black/65" />

        {/* glows */}
        <div className="absolute -top-24 -left-20 w-[280px] h-[280px] rounded-full bg-amber-400/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-[260px] h-[260px] rounded-full bg-cyan-400/15 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm mb-6">
              <Sparkles className="w-4 h-4 text-amber-300" />
              About GLGIF
            </span>

            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Our Values
            </h1>

            <p className="text-lg md:text-xl text-stone-200 max-w-2xl leading-relaxed">
              The core principles that guide everything we do at GLGIF, shaping how we serve
              and interact with the communities we are privileged to support.
            </p>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-accent text-xl text-amber-700 mb-3">What We Stand For</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900">
              Principles That Shape Our Impact
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {values.map((value, index) => (
              <Card
                key={value.title}
                className="value-card rounded-2xl border-stone-200/80 shadow-sm group bg-white"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8">
                  <div
                    className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center mb-6 value-icon`}
                  >
                    <value.icon className="w-8 h-8" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-stone-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visual strip */}
      <section className="pb-10 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm">
              <img
                src="/assets/values-support-1.png"
                alt="Community support initiative"
                className="w-full h-[260px] object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm">
              <img
                src="/assets/values-support-2.png"
                alt="Volunteers serving in community"
                className="w-full h-[260px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-6">
            <HandHeart className="w-8 h-8" />
          </div>

          <blockquote className="font-heading text-2xl md:text-3xl text-stone-800 mb-6 leading-relaxed">
            “A true philanthropist is the one who has just 1 piece of bread but still chooses
            to share it among others so they too can have a bite.”
          </blockquote>

          <p className="text-stone-600">— GLGIF Founding Principle</p>
        </div>
      </section>

      {/* Living Our Values */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl border border-stone-200 bg-white shadow-sm p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">
                Living Our Values
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                At GLGIF, our values are not slogans—they are the standard for how we lead,
                serve, and sustain meaningful change.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-8">
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5" />
                <p className="text-stone-700">We serve with empathy, not pity.</p>
              </div>
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5" />
                <p className="text-stone-700">We protect dignity in every intervention.</p>
              </div>
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5" />
                <p className="text-stone-700">We measure success by transformed lives.</p>
              </div>
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5" />
                <p className="text-stone-700">We remain accountable to communities and partners.</p>
              </div>
            </div>

            <p className="text-lg text-stone-600 text-center leading-relaxed">
              Whether you volunteer, partner, or support our work, you become part of a
              purpose-driven community committed to lasting impact.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Values;
